/**
 * `/finance` — provider reconciliation (§10.3 step 5, §10.5): runs, the exception queue with age / amount / owner /
 * attempts / next action and a participant-safe explanation, resolution with a reason, on-demand runs.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReconciliationExceptionRowDto } from '@windrop/contracts';
import { Button, Field, Input, Modal, Select, useIntl, useToast } from '@windrop/ui';
import { useState } from 'react';
import { Can } from '../../auth/guards';
import { DataTable } from '../../components/DataTable';
import { ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { QueueList, type QueueItem } from '../../components/QueueList';
import { StatCard } from '../../components/StatCard';
import { ToneBadge, type Tone } from '../../components/StatusBadge';
import { financeApi } from '../../lib/api/finance';
import { isApiError } from '../../lib/api/client';
import { FinanceNav } from './FinanceNav';

export function ReconciliationPage() {
  const intl = useIntl();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [lotteryId, setLotteryId] = useState('');
  const [resolving, setResolving] = useState<ReconciliationExceptionRowDto | null>(null);
  const [status, setStatus] = useState<'INVESTIGATING' | 'RESOLVED' | 'WRITTEN_OFF'>('RESOLVED');
  const [resolution, setResolution] = useState('');
  const [error, setError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ['finance', 'reconciliation', lotteryId],
    queryFn: () => financeApi.reconciliation(lotteryId ? { lotteryId } : undefined),
  });
  const resolve = useMutation({
    mutationFn: () =>
      financeApi.resolveException(resolving!.exceptionId, { status, resolution: resolution.trim() }),
    onSuccess: () => {
      setResolving(null);
      setResolution('');
      void qc.invalidateQueries({ queryKey: ['finance'] });
      toast({ title: 'Exception updated', tone: 'success' });
    },
    onError: (e) => setError(isApiError(e) ? e.message : 'Could not update the exception.'),
  });
  const run = useMutation({
    mutationFn: () =>
      financeApi.runReconciliation({ trigger: 'manual', ...(lotteryId ? { lotteryId } : {}) }),
    onSuccess: (r) => toast({ title: `Reconciliation run queued (${r.jobId})`, tone: 'success' }),
    onError: (e) => toast({ title: isApiError(e) ? e.message : 'Could not queue the run', tone: 'neutral' }),
  });
  const data = query.data;
  const items: QueueItem[] = (data?.exceptions ?? []).map((e) => ({
    id: e.exceptionId,
    title: `${e.kind.replace(/_/g, ' ')}${e.expected ? ` · expected ${intl.formatMoney(e.expected.amountMinor, e.expected.currency)}` : ''}${e.actual ? ` · actual ${intl.formatMoney(e.actual.amountMinor, e.actual.currency)}` : ''}`,
    subtitle: `${e.participantSafeExplanation} · attempts ${e.attempts}${e.paymentId ? ` · payment ${e.paymentId}` : ''}`,
    since: e.createdAt,
    owner: e.owner,
    status: {
      tone: (e.blocksDraw ? 'error' : e.status === 'INVESTIGATING' ? 'warning' : 'info') as Tone,
      text: e.blocksDraw ? 'Blocks draw' : e.status,
    },
    nextAction: {
      label: e.nextAction.length > 48 ? 'Work exception' : e.nextAction,
      onClick: () => {
        setError(null);
        setResolving(e);
      },
    },
  }));
  return (
    <>
      <PageHeader
        title="Finance — reconciliation"
        description="Intents ↔ provider ↔ ledger ↔ entries ↔ refunds ↔ payouts. Discrepancies that touch eligibility block the affected draw until resolved with a reason."
        breadcrumb={[{ label: 'Finance' }, { label: 'Reconciliation' }]}
        actions={
          <Can permission="finance.reconcile">
            <Button onClick={() => run.mutate()} loading={run.isPending} loadingLabel="Queuing run">
              Run reconciliation now
            </Button>
          </Can>
        }
      />
      <FinanceNav />
      <Field label="Lottery id filter" hint="Leave empty for every lottery of this organizer.">
        <Input
          value={lotteryId}
          onChange={(e) => setLotteryId(e.target.value.trim())}
          data-testid="recon-lottery-filter"
        />
      </Field>
      {query.isError ? (
        <ErrorState
          title="Reconciliation unavailable"
          body="Retry in a moment."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : null}
      <div className="wdc-grid">
        <StatCard
          label="Open exceptions"
          value={data ? data.openExceptions : null}
          loading={query.isPending}
          signal={
            data && data.blockingExceptions > 0
              ? { tone: 'error', text: `${data.blockingExceptions} block a draw` }
              : undefined
          }
        />
        <StatCard
          label="Ledger"
          value={data ? (data.ledgerBalanced ? 'Balanced' : 'IMBALANCED') : null}
          loading={query.isPending}
          signal={
            data
              ? {
                  tone: data.ledgerBalanced ? 'success' : 'error',
                  text: data.ledgerBalanced ? 'Σdebits = Σcredits' : 'Sev-1: post a correcting reversal',
                }
              : undefined
          }
        />
        <StatCard
          label="Last run"
          value={data ? (data.lastRun ? intl.formatDateTime(data.lastRun.startedAt) : null) : null}
          loading={query.isPending}
          hint={
            data?.lastRun
              ? `${data.lastRun.trigger} · ${data.lastRun.exceptionCount} exception(s)`
              : undefined
          }
          emptyHint="No reconciliation run yet (daily 02:30 UTC or on draw)."
        />
      </div>
      <section aria-labelledby="recon-queue" style={{ marginTop: 'var(--wd-space-5)' }}>
        <h2 id="recon-queue">Exception queue</h2>
        <QueueList
          items={items}
          emptyText="No open exceptions — intents, provider, ledger and entries agree."
        />
      </section>
      <section aria-labelledby="recon-runs" style={{ marginTop: 'var(--wd-space-5)' }}>
        <h2 id="recon-runs">Runs</h2>
        <DataTable
          caption="Reconciliation runs"
          rows={data?.runs ?? []}
          rowKey={(r) => r.runId}
          loading={query.isPending}
          pageSize={10}
          filterable={false}
          columns={[
            {
              id: 'startedAt',
              header: 'Started',
              cell: (r) => intl.formatDateTime(r.startedAt),
              sortValue: (r) => r.startedAt,
            },
            { id: 'trigger', header: 'Trigger', cell: (r) => r.trigger },
            { id: 'provider', header: 'Provider', cell: (r) => r.provider },
            {
              id: 'exceptions',
              header: 'Exceptions',
              cell: (r) => r.exceptionCount,
              sortValue: (r) => r.exceptionCount,
              align: 'end',
            },
            {
              id: 'blocks',
              header: 'Draw',
              cell: (r) => (
                <ToneBadge tone={r.blocksDraw ? 'error' : 'success'}>
                  {r.blocksDraw ? 'Blocked' : 'Clear'}
                </ToneBadge>
              ),
            },
          ]}
        />
      </section>
      <Modal
        open={resolving !== null}
        onOpenChange={(o) => {
          if (!o) setResolving(null);
        }}
        title={resolving ? `Work exception: ${resolving.kind.replace(/_/g, ' ')}` : 'Exception'}
        description={resolving?.nextAction}
      >
        {resolving ? (
          <div className="wd-stack">
            <p className="wd-muted">Participant sees: “{resolving.participantSafeExplanation}”</p>
            <Field label="Outcome" required>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value as typeof status)}
                options={[
                  { value: 'INVESTIGATING', label: 'Take ownership (investigating)' },
                  { value: 'RESOLVED', label: 'Resolved' },
                  { value: 'WRITTEN_OFF', label: 'Written off' },
                ]}
              />
            </Field>
            <Field
              label="Reason / evidence"
              required
              hint="At least 10 characters; written to the audit trail."
            >
              <Input
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                data-testid="recon-resolution"
              />
            </Field>
            {error ? (
              <p role="alert" className="text-error">
                {error}
              </p>
            ) : null}
            <div className="wd-btn-group">
              <Button
                onClick={() => {
                  if (resolution.trim().length < 10) {
                    setError('Give a reason of at least 10 characters.');
                    return;
                  }
                  resolve.mutate();
                }}
                loading={resolve.isPending}
                loadingLabel="Saving"
              >
                Save decision
              </Button>
              <Button variant="secondary" onClick={() => setResolving(null)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
}
